import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';
import {
  Science as ScienceIcon,
  Videocam as VideocamIcon,
  Storage as StorageIcon,
  Folder as FolderIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';

const SettingsView: React.FC = () => {
  // Randomized connection states for integrations
  const [connectionStates] = React.useState({
    forensicLabs: 'connected', // Green - active connection
    cctvSystems: 'error', // Red - connection error
    drivesFolders: 'disconnected', // Blue - no connection yet
    mobileDevices: 'connected', // Green - active connection
    governmentAgencies: 'disconnected' // Blue - no connection yet
  });

  const getConnectionStatus = (status: string) => {
    switch (status) {
      case 'connected':
        return { color: 'success.main', icon: <CheckCircleIcon />, label: 'Connected' };
      case 'error':
        return { color: 'error.main', icon: <ErrorIcon />, label: 'Connection Error' };
      case 'disconnected':
      default:
        return { color: 'primary.main', icon: <CloudUploadIcon />, label: 'Connect' };
    }
  };

  return (
    <Box sx={{ height: '100%', padding: '20px', overflow: 'auto' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: '#333' }}>Settings</h2>
        <p style={{ margin: '8px 0', color: '#666' }}>
          Manage system integrations and configurations
        </p>
      </div>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
        {/* Forensic Labs */}
        <Card sx={{ boxShadow: 2, '&:hover': { boxShadow: 4 } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <ScienceIcon sx={{ mr: 1, color: 'primary.main', fontSize: 24 }} />
              <Typography variant="h6">Forensic Labs</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Import forensic analysis reports, DNA results, fingerprint data, and laboratory findings
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={getConnectionStatus(connectionStates.forensicLabs).icon}
                sx={{ 
                  color: getConnectionStatus(connectionStates.forensicLabs).color,
                  borderColor: getConnectionStatus(connectionStates.forensicLabs).color
                }}
              >
                {getConnectionStatus(connectionStates.forensicLabs).label}
              </Button>
              <Button variant="outlined" size="small">
                Import Files
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* CCTV Systems */}
        <Card sx={{ boxShadow: 2, '&:hover': { boxShadow: 4 } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <VideocamIcon sx={{ mr: 1, color: 'primary.main', fontSize: 24 }} />
              <Typography variant="h6">CCTV Systems</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Import video footage, camera metadata, and surveillance system data
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={getConnectionStatus(connectionStates.cctvSystems).icon}
                sx={{ 
                  color: getConnectionStatus(connectionStates.cctvSystems).color,
                  borderColor: getConnectionStatus(connectionStates.cctvSystems).color
                }}
              >
                {getConnectionStatus(connectionStates.cctvSystems).label}
              </Button>
              <Button variant="outlined" size="small">
                Import Videos
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Drives and Folders */}
        <Card sx={{ boxShadow: 2, '&:hover': { boxShadow: 4 } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <StorageIcon sx={{ mr: 1, color: 'primary.main', fontSize: 24 }} />
              <Typography variant="h6">Drives & Folders</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Import files from local drives, network folders, and cloud storage systems
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={getConnectionStatus(connectionStates.drivesFolders).icon}
                sx={{ 
                  color: getConnectionStatus(connectionStates.drivesFolders).color,
                  borderColor: getConnectionStatus(connectionStates.drivesFolders).color
                }}
              >
                {getConnectionStatus(connectionStates.drivesFolders).label}
              </Button>
              <Button variant="outlined" size="small">
                Import Folder
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Mobile Devices */}
        <Card sx={{ boxShadow: 2, '&:hover': { boxShadow: 4 } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FolderIcon sx={{ mr: 1, color: 'primary.main', fontSize: 24 }} />
              <Typography variant="h6">Mobile Devices</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Import data from phones, tablets, and other mobile devices
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={getConnectionStatus(connectionStates.mobileDevices).icon}
                sx={{ 
                  color: getConnectionStatus(connectionStates.mobileDevices).color,
                  borderColor: getConnectionStatus(connectionStates.mobileDevices).color
                }}
              >
                {getConnectionStatus(connectionStates.mobileDevices).label}
              </Button>
              <Button variant="outlined" size="small">
                Import Data
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Government Agencies */}
        <Card sx={{ boxShadow: 2, '&:hover': { boxShadow: 4 } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <StorageIcon sx={{ mr: 1, color: 'primary.main', fontSize: 24 }} />
              <Typography variant="h6">Government Agencies</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Import data from HDB, BCA, LTA, NEA, and other Singapore government agencies
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button 
                variant="outlined" 
                size="small" 
                startIcon={getConnectionStatus(connectionStates.governmentAgencies).icon}
                sx={{ 
                  color: getConnectionStatus(connectionStates.governmentAgencies).color,
                  borderColor: getConnectionStatus(connectionStates.governmentAgencies).color
                }}
              >
                {getConnectionStatus(connectionStates.governmentAgencies).label}
              </Button>
              <Button variant="outlined" size="small">
                Import Records
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Recent Imports */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
          Recent Imports
        </Typography>
        <TableContainer component={Paper} sx={{ maxHeight: 200 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Source</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Forensic Lab A</TableCell>
                <TableCell>DNA Analysis</TableCell>
                <TableCell>2024-01-15</TableCell>
                <TableCell>
                  <Chip label="Completed" size="small" color="success" />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>CCTV System B</TableCell>
                <TableCell>Video Footage</TableCell>
                <TableCell>2024-01-14</TableCell>
                <TableCell>
                  <Chip label="Failed" size="small" color="error" />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Mobile Device X</TableCell>
                <TableCell>Call Logs</TableCell>
                <TableCell>2024-01-13</TableCell>
                <TableCell>
                  <Chip label="Completed" size="small" color="success" />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default SettingsView;
